import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import { adminApi } from "../../services/adminApi";
import { packageService } from "../../services/packageService";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import {
  FiDownload, FiTrendingUp, FiTrendingDown, FiUsers,
  FiDollarSign, FiVideo, FiActivity, FiCheckCircle,
  FiSearch, FiChevronLeft, FiChevronRight, FiFilter,
  FiChevronDown, FiGlobe, FiAward, FiShield, FiClock,
  FiCalendar, FiGrid, FiMapPin, FiUserCheck, FiX, FiCheck,
  FiLayers, FiTv, FiArrowRight, FiInfo, FiChevronsLeft, FiChevronsRight
} from "react-icons/fi";
import { WORLD_MAP_PATHS } from "./worldMapData";

// ─── COLOR TOKENS ─────────────────────────────────────────────────────────────
const T = {
  olive: "#6B8A5A", olive2: "#8BA47A",
  orange: "#F29A0C", orange2: "#E97818",
  dark: "#1F1F1F", mid: "#555555", muted: "#8E8E93", light: "#AEAEB2",
  border: "rgba(239,229,216,0.85)",
  green: "#22C55E", greenDk: "#16A34A",
  red: "#E24B4A", amber: "#F59E0B", blue: "#0284C7", purple: "#534AB7",
};

const glass = {
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 18px 50px rgba(0,0,0,0.04), inset 0 1px 0 #fff",
};

const TIER_THEMES = {
  "free trial": { color: "#D97706", grad: "linear-gradient(135deg, #FBBF24, #D97706)", bg: "rgba(245,158,11,0.12)", text: "#B45309" },
  "basic":      { color: "#6B8A5A", grad: "linear-gradient(135deg, #8BA47A, #4D683E)", bg: "rgba(107,138,90,0.12)", text: "#4D683E" },
  "standard":   { color: "#0284C7", grad: "linear-gradient(135deg, #38BDF8, #0369A1)", bg: "rgba(2,132,199,0.12)",  text: "#0369A1" },
  "premium":    { color: "#059669", grad: "linear-gradient(135deg, #34D399, #047857)", bg: "rgba(5,150,105,0.12)",  text: "#047857" },
  "ytt pro":    { color: "#E97818", grad: "linear-gradient(135deg, #F29A0C, #C8660A)", bg: "rgba(242,154,12,0.12)", text: "#C8660A" },
  "ytt":        { color: "#E97818", grad: "linear-gradient(135deg, #F29A0C, #C8660A)", bg: "rgba(242,154,12,0.12)", text: "#C8660A" },
  "vip":        { color: "#9333EA", grad: "linear-gradient(135deg, #C084FC, #7E22CE)", bg: "rgba(147,51,234,0.12)", text: "#7E22CE" },
  "expired":    { color: "#E24B4A", grad: "linear-gradient(135deg, #F87171, #DC2626)", bg: "rgba(226,75,74,0.12)",  text: "#B91C1C" },
  "gold plan":  { color: "#D97706", grad: "linear-gradient(135deg, #FDE68A, #D97706)", bg: "rgba(217,119,6,0.12)",  text: "#92400E" }
};

const HOURLY_SLOTS = [
  { label: "6:00 AM", hour: 6 },
  { label: "7:00 AM", hour: 7 },
  { label: "8:00 AM", hour: 8 },
  { label: "9:00 AM", hour: 9 },
  { label: "10:00 AM", hour: 10 },
  { label: "11:00 AM", hour: 11 },
  { label: "12:00 PM", hour: 12 },
  { label: "1:00 PM", hour: 13 },
  { label: "2:00 PM", hour: 14 },
  { label: "3:00 PM", hour: 15 },
  { label: "4:00 PM", hour: 16 },
  { label: "5:00 PM", hour: 17 },
  { label: "6:00 PM", hour: 18 },
  { label: "7:00 PM", hour: 19 },
  { label: "8:00 PM", hour: 20 },
];

const WEEKDAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ─── AIRTIGHT CANONICAL TIER MATCHER ──────────────────────────────────────────
function isTierMatch(userTier, filterValue) {
  if (!filterValue || filterValue === "all") return true;
  if (!userTier) return false;
  const u = String(userTier).toLowerCase().trim();
  const f = String(filterValue).toLowerCase().trim();
  if (u === f) return true;
  if (f === "free trial" && (u.includes("free") || u.includes("trial"))) return true;
  if (f === "ytt pro" && (u.includes("ytt") || u.includes("pro"))) return true;
  if (f === "standard" && u.includes("standard")) return true;
  if (f === "basic" && u.includes("basic")) return true;
  if (f === "premium" && u.includes("premium")) return true;
  if (f === "expired" && u.includes("expired")) return true;
  if (f === "vip" && u.includes("vip")) return true;
  return u.includes(f) || f.includes(u);
}

// ─── TREND BADGE ─────────────────────────────────────────────────────────────
const TrendBadge = ({ val, up, positive = true }) => {
  const isGood = positive ? up : !up;
  return (
    <span className="text-[11.5px] font-extrabold flex items-center gap-1" style={{ color: isGood ? T.greenDk : T.red }}>
      {up ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}{val}
    </span>
  );
};

// ─── STAT CARD ───────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, value, label, trend, trendUp, positive = true, iconBg, accentColor, cardBg, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    whileHover={{ y: -4, boxShadow: "0 24px 55px rgba(0,0,0,0.08)" }}
    className="ad-kpi-card relative overflow-hidden rounded-[24px] p-5 border flex flex-col justify-between"
    style={{ background: cardBg || "rgba(255,255,255,0.94)", borderColor: "rgba(239,229,216,0.9)", transition: "all 0.28s" }}>
    <div className="ad-kpi-body">
      <div className="ad-kpi-iconrow flex items-center justify-between mb-3.5">
        <div className="ad-kpi-icon w-10 h-10 rounded-[14px] flex items-center justify-center shadow-xs" style={{ background: iconBg }}>
          <Icon size={18} style={{ color: "#fff" }} />
        </div>
        <span className="ad-kpi-note"><TrendBadge val={trend} up={trendUp} positive={positive} /></span>
      </div>
      <p className="ad-kpi-val font-black text-[#1F1F1F] mb-1"
        style={{ fontFamily: "'Poppins',sans-serif", fontSize: 25, letterSpacing: "-0.8px", lineHeight: 1.1 }}>
        {value}
      </p>
      <p className="ad-kpi-lbl text-[11px] font-extrabold uppercase tracking-[0.9px]" style={{ color: T.muted }}>
        {label}
      </p>
    </div>
    <div className="ad-kpi-note mt-4 pt-2 border-t border-zinc-100 flex items-center justify-between text-[11px] font-bold text-zinc-400">
      <span>Real-time</span>
      <span className="w-2 h-2 rounded-full bg-emerald-500" />
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-[4px] rounded-b-[24px]" style={{ background: accentColor }} />
  </motion.div>
);

// ─── CUSTOM LUXURY DROPDOWN ──────────────────────────────────────────────────
function CustomDropdown({ value, options, onChange, placeholder = "Select...", width = "w-56" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value) || { label: placeholder, value };

  return (
    <div className={`relative ${width}`} style={{ zIndex: open ? 9999 : 50 }} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full h-9 px-3.5 rounded-full bg-white/95 border border-amber-900/15 shadow-2xs flex items-center justify-between text-[12px] font-bold text-zinc-800 hover:border-amber-500/60 transition-all cursor-pointer select-none"
      >
        <span className="truncate flex items-center gap-1.5">
          {selectedOption.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: selectedOption.color }} />}
          {selectedOption.shortLabel || selectedOption.label}
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} className="text-zinc-400 shrink-0 ml-1">
          <FiChevronDown size={14} />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="ad-dd-menu absolute left-0 right-0 top-full mt-1.5 rounded-[18px] bg-white border border-amber-900/15 shadow-2xl p-1.5 backdrop-blur-xl max-h-64 overflow-y-auto"
            style={{ zIndex: 99999, minWidth: 230 }}
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <div
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`px-3 py-2 rounded-[12px] text-[12px] font-bold flex items-center justify-between cursor-pointer transition-all ${
                    isSelected ? "bg-amber-500 text-white shadow-xs" : "text-zinc-700 hover:bg-amber-50 hover:text-amber-900"
                  }`}
                >
                  <span className="flex items-center gap-2 truncate">
                    {opt.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: isSelected ? "#fff" : opt.color }} />}
                    {opt.label}
                  </span>
                  {isSelected && <FiCheckCircle size={13} className="shrink-0" />}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── REVENUE LINE CHART ───────────────────────────────────────────────────────
const RevenueLineChart = ({ data = [], labels = [], unit = "₹" }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const maxVal = Math.max(1000, ...data);

  const W = 620, H = 160, PAD_L = 55, PAD_R = 20, PAD_T = 20, PAD_B = 30;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const pts = data.map((v, i) => {
    const x = PAD_L + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2);
    const y = PAD_T + chartH - (v / maxVal) * chartH;
    return [x, y];
  });

  const path = pts.length > 0 ? "M" + pts.map((p) => p.join(",")).join(" L") : "";
  const fill = pts.length > 0 ? path + ` L${pts[pts.length - 1][0]},${PAD_T + chartH} L${pts[0][0]},${PAD_T + chartH} Z` : "";

  const formatScale = (val) => {
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}k`;
    return `₹${Math.round(val)}`;
  };

  return (
    <div className="relative w-full select-none" onMouseLeave={() => setHoveredIdx(null)}>
      {hoveredIdx !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute z-30 pointer-events-none px-3.5 py-2 rounded-[14px] text-white shadow-xl bg-zinc-900/95 border border-white/20 backdrop-blur-md"
          style={{
            left: `${(pts[hoveredIdx][0] / W) * 100}%`,
            top: `${Math.max(5, (pts[hoveredIdx][1] / H) * 100 - 32)}%`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <p className="text-[10px] font-bold text-zinc-300 uppercase tracking-wider">{labels[hoveredIdx]}</p>
          <p className="text-[14px] font-black text-amber-400 font-poppins">
            {unit}{Number(data[hoveredIdx] || 0).toLocaleString()}
          </p>
        </motion.div>
      )}

      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.orange} stopOpacity="0.28" />
            <stop offset="100%" stopColor={T.orange} stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {[1, 0.66, 0.33, 0].map((f) => {
          const yPos = PAD_T + (1 - f) * chartH;
          return (
            <g key={f}>
              <line x1={PAD_L} y1={yPos} x2={W - PAD_R} y2={yPos} stroke="rgba(239,229,216,0.8)" strokeDasharray={f === 0 ? "none" : "3,3"} strokeWidth="1" />
              <text x={PAD_L - 10} y={yPos + 3.5} textAnchor="end" fill={T.muted} fontSize="9.5" fontWeight="700" fontFamily="Inter, sans-serif">
                {formatScale(maxVal * f)}
              </text>
            </g>
          );
        })}

        {fill && <path d={fill} fill="url(#revGrad)" />}

        {path && (
          <motion.path
            d={path}
            fill="none"
            stroke={T.orange}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          />
        )}

        {hoveredIdx !== null && (
          <line
            x1={pts[hoveredIdx][0]}
            y1={PAD_T}
            x2={pts[hoveredIdx][0]}
            y2={PAD_T + chartH}
            stroke={T.orange}
            strokeWidth="1.5"
            strokeDasharray="3,3"
          />
        )}

        {pts.map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="18" fill="transparent" className="cursor-pointer" onMouseEnter={() => setHoveredIdx(i)} />
            <circle
              cx={x}
              cy={y}
              r={hoveredIdx === i ? 6 : 4}
              fill={hoveredIdx === i ? "#E97818" : T.orange}
              stroke="#FFFFFF"
              strokeWidth="2"
              className="transition-all"
            />
            {hoveredIdx === i && <circle cx={x} cy={y} r={11} fill={T.orange} opacity="0.3" className="animate-ping" />}
          </g>
        ))}
      </svg>

      <div className="flex justify-between items-center pt-1" style={{ paddingLeft: PAD_L, paddingRight: PAD_R }}>
        {labels.map((l, i) => (
          <span key={i} className="text-[10px] font-extrabold text-zinc-400 text-center flex-1">
            {l}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── USER GROWTH BAR CHART ────────────────────────────────────────────────────
const UserGrowthBarChart = ({ data = [], labels = [], color = T.olive }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const maxVal = Math.max(1, ...data);

  return (
    <div className="flex flex-col justify-end h-[160px] pt-4 select-none relative" onMouseLeave={() => setHoveredIdx(null)}>
      {hoveredIdx !== null && (
        <div className="absolute top-0 right-2 px-3 py-1 rounded-full bg-zinc-900 text-white text-[11px] font-bold shadow-md z-20">
          ● {labels[hoveredIdx]}: <span className="text-emerald-400 font-extrabold">+{data[hoveredIdx]} registrations</span>
        </div>
      )}

      <div className="flex items-end gap-2.5 flex-1 relative pb-2 border-b border-zinc-200/70">
        {data.map((v, i) => {
          const isSelected = hoveredIdx === i;
          const barHeight = maxVal > 0 ? (v / maxVal) * 100 : 0;

          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative cursor-pointer" onMouseEnter={() => setHoveredIdx(i)}>
              <motion.div
                className="w-full rounded-t-[7px] transition-all"
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(barHeight, 6)}%` }}
                transition={{ duration: 0.9, delay: i * 0.04, ease: "easeOut" }}
                style={
                  isSelected
                    ? { background: "linear-gradient(180deg, #8BA47A, #4D683E)", boxShadow: "0 6px 18px rgba(107,138,90,0.5)", transform: "scaleX(1.12)" }
                    : { background: `${color}38` }
                }
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center pt-2">
        {labels.map((l, i) => (
          <span key={i} className="flex-1 text-center truncate text-[10px] font-extrabold text-zinc-400">
            {l}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── SUBSCRIPTION DONUT CHART ────────────────────────────────────────────────
const SubscriptionDonutChart = ({ tiers = [], totalCount = 0 }) => {
  const R = 50, C = 2 * Math.PI * R;
  let offset = 0;

  const validTiers = (Array.isArray(tiers) ? tiers : []).filter(t => t && (t.n > 0 || t.pct > 0 || tiers.length <= 4));

  const slices = validTiers.map((d) => {
    const len = (C * (d.pct || 0)) / 100;
    const theme = TIER_THEMES[d.tier.toLowerCase()] || TIER_THEMES["standard"] || { color: "#6B8A5A" };
    const s = { ...d, offset, len, color: d.color || theme.color };
    offset += len;
    return s;
  });

  const [hovered, setHovered] = useState(null);
  const activeSlice = hovered !== null ? slices[hovered] : null;

  return (
    <div className="relative w-[150px] h-[150px] mx-auto flex items-center justify-center">
      <svg viewBox="0 0 120 120" className="absolute inset-0 w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(239,229,216,0.6)" strokeWidth="14" />
        {slices.map((s, i) => (
          <motion.circle
            key={s.tier}
            cx="60" cy="60" r={R} fill="none"
            stroke={s.color} strokeWidth={hovered === i ? 17 : 14}
            strokeDasharray={`${s.len} ${C - s.len}`}
            strokeDashoffset={-s.offset} strokeLinecap="round"
            initial={{ strokeDasharray: `0 ${C}` }}
            animate={{ strokeDasharray: `${s.len} ${C - s.len}` }}
            transition={{ duration: 1.1, delay: 0.1 + i * 0.08, ease: "easeOut" }}
            className="cursor-pointer transition-all"
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
          />
        ))}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
        {activeSlice ? (
          <>
            <p className="font-black text-[20px] leading-tight" style={{ color: activeSlice.color, fontFamily: "'Poppins',sans-serif" }}>
              {activeSlice.pct}%
            </p>
            <p className="text-[9.5px] font-extrabold text-zinc-700 max-w-[85px] truncate">{activeSlice.tier}</p>
          </>
        ) : (
          <>
            <p className="font-black text-[22px] leading-tight text-zinc-900" style={{ fontFamily: "'Poppins',sans-serif" }}>
              {totalCount || 0}
            </p>
            <p className="text-[9px] font-black uppercase text-zinc-400 tracking-wider">Active Users</p>
          </>
        )}
      </div>
    </div>
  );
};

// ─── SAAS ATTENDANCE CALENDAR & REAL-DATE WEEKLY SUITE ─────────────────────────
function SaaSAttendanceCalendarSuite({ heatmapData, dynamicTierOptions = [] }) {
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "weekly"
  const [typeFilter, setTypeFilter] = useState("all"); // "all" | "class" | "session"
  const [mainTierFilter, setMainTierFilter] = useState("all"); // Filter by tier on MAIN heatmap
  const [selectedMonth, setSelectedMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1)); // Dynamic current month default
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week
  const [activeDayDetails, setActiveDayDetails] = useState(null);
  const [hoveredInfo, setHoveredInfo] = useState(null);

  // Modal Internal Filter States
  const [modalTypeTab, setModalTypeTab] = useState("all"); // "all" | "class" | "session"
  const [modalTierFilter, setModalTierFilter] = useState("all");

  const allRawLogs = useMemo(() => heatmapData?.calendarLogs || [], [heatmapData]);

  // Build 100% Dynamic Tier Options with UNIQUE USER counts from Database
  const heatmapTierOptions = useMemo(() => {
    // 1. Group logs by user to find unique students
    const totalUsersSet = new Set(allRawLogs.map((l) => l.userId || l.userName));
    const totalUniqueUsers = totalUsersSet.size;

    const opts = [{
      label: `All Tiers (${totalUniqueUsers} Unique Users · ${allRawLogs.length} Check-Ins)`,
      shortLabel: `All Tiers (${totalUniqueUsers} Unique Users)`,
      value: "all",
      uniqueUsers: totalUniqueUsers,
      checkins: allRawLogs.length
    }];

    // Known tiers + dynamic detection from live logs
    const tierDefs = [
      { name: "Standard", val: "standard", color: TIER_THEMES.standard.color },
      { name: "Free Trial", val: "free trial", color: TIER_THEMES["free trial"].color },
      { name: "YTT Pro", val: "ytt pro", color: TIER_THEMES["ytt pro"].color },
      { name: "Basic", val: "basic", color: TIER_THEMES.basic.color },
      { name: "Premium", val: "premium", color: TIER_THEMES.premium.color },
      { name: "VIP", val: "vip", color: TIER_THEMES.vip.color },
      { name: "Expired", val: "expired", color: TIER_THEMES.expired.color },
    ];

    tierDefs.forEach((td) => {
      // Find matching users and check-ins using isTierMatch
      const matchingLogs = allRawLogs.filter((l) => isTierMatch(l.userTier, td.val));
      const uniqueUsersSet = new Set(matchingLogs.map((l) => l.userId || l.userName));
      const uCount = uniqueUsersSet.size;
      const cCount = matchingLogs.length;

      opts.push({
        label: uCount > 0 ? `${td.name} (${uCount} Unique ${uCount === 1 ? "User" : "Users"} · ${cCount} Check-Ins)` : `${td.name} (0 Users)`,
        shortLabel: `${td.name} (${uCount} ${uCount === 1 ? "User" : "Users"})`,
        value: td.val,
        color: td.color,
        uniqueUsers: uCount,
        checkins: cCount
      });
    });

    return opts;
  }, [allRawLogs]);

  // Intelligent Handler for Setting Main Tier Filter with Auto Month Navigation
  const handleSelectMainTier = (newTier) => {
    setMainTierFilter(newTier);

    if (newTier === "all") {
      const now = new Date();
      setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      setWeekOffset(0);
      return;
    }

    const matchingLogs = allRawLogs.filter((l) => isTierMatch(l.userTier, newTier));
    if (matchingLogs.length === 0) return;

    const now = new Date();
    const currentMonthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const hasCurrentMonthRecords = matchingLogs.some((l) => l.date && l.date.startsWith(currentMonthPrefix));

    if (!hasCurrentMonthRecords && matchingLogs.length > 0) {
      const sortedLogs = [...matchingLogs].sort((a, b) => new Date(b.date) - new Date(a.date));
      const latestDate = new Date(sortedLogs[0].date);
      if (!isNaN(latestDate.getTime())) {
        setSelectedMonth(new Date(latestDate.getFullYear(), latestDate.getMonth(), 1));
        setWeekOffset(0);
      }
    } else {
      setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      setWeekOffset(0);
    }
  };

  // Filter raw logs by BOTH typeFilter AND mainTierFilter using isTierMatch
  const filteredLogsForMainView = useMemo(() => {
    return allRawLogs.filter((item) => {
      // 1. Type filter
      if (typeFilter === "class" && item.type !== "In-Studio Check-in" && item.type !== "Class Check-in") return false;
      if (typeFilter === "session" && item.type !== "Live Session Check-in" && item.type !== "Live Session") return false;

      // 2. Main tier filter with fuzzy canonical matching
      if (!isTierMatch(item.userTier, mainTierFilter)) {
        return false;
      }
      return true;
    });
  }, [allRawLogs, typeFilter, mainTierFilter]);

  // Group filtered logs by date
  const logsByDate = useMemo(() => {
    const map = {};
    filteredLogsForMainView.forEach((item) => {
      if (!map[item.date]) map[item.date] = [];
      map[item.date].push(item);
    });
    return map;
  }, [filteredLogsForMainView]);

  // Dynamic check-in counts & UNIQUE USER COUNTS based on mainTierFilter
  const tierScopedTotalCount = useMemo(() => {
    return allRawLogs.filter((item) => isTierMatch(item.userTier, mainTierFilter)).length;
  }, [allRawLogs, mainTierFilter]);

  const tierScopedUniqueUserCount = useMemo(() => {
    const matching = allRawLogs.filter((item) => isTierMatch(item.userTier, mainTierFilter));
    return new Set(matching.map((l) => l.userId || l.userName)).size;
  }, [allRawLogs, mainTierFilter]);

  const tierScopedUserClassCount = useMemo(() => {
    return allRawLogs.filter((item) => {
      const matchType = item.type === "In-Studio Check-in" || item.type === "Class Check-in";
      return matchType && isTierMatch(item.userTier, mainTierFilter);
    }).length;
  }, [allRawLogs, mainTierFilter]);

  const tierScopedStudioUniqueUserCount = useMemo(() => {
    const matching = allRawLogs.filter((item) => {
      const matchType = item.type === "In-Studio Check-in" || item.type === "Class Check-in";
      return matchType && isTierMatch(item.userTier, mainTierFilter);
    });
    return new Set(matching.map((l) => l.userId || l.userName)).size;
  }, [allRawLogs, mainTierFilter]);

  const tierScopedLiveSessionCount = useMemo(() => {
    return allRawLogs.filter((item) => {
      const matchType = item.type === "Live Session Check-in" || item.type === "Live Session";
      return matchType && isTierMatch(item.userTier, mainTierFilter);
    }).length;
  }, [allRawLogs, mainTierFilter]);

  const tierScopedLiveUniqueUserCount = useMemo(() => {
    const matching = allRawLogs.filter((item) => {
      const matchType = item.type === "Live Session Check-in" || item.type === "Live Session";
      return matchType && isTierMatch(item.userTier, mainTierFilter);
    });
    return new Set(matching.map((l) => l.userId || l.userName)).size;
  }, [allRawLogs, mainTierFilter]);

  // Find which months have check-ins for the currently selected tier
  const tierActiveMonths = useMemo(() => {
    if (mainTierFilter === "all") return [];
    const monthsSet = new Set();
    allRawLogs.filter((item) => isTierMatch(item.userTier, mainTierFilter)).forEach((item) => {
      const d = new Date(item.date);
      monthsSet.add(`${d.getFullYear()}-${d.getMonth()}`);
    });
    return Array.from(monthsSet);
  }, [allRawLogs, mainTierFilter]);

  // Today reference (Live Dynamic Date)
  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }, []);

  // Month navigation
  const prevMonth = () => setSelectedMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setSelectedMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const setMonthToday = () => {
    const now = new Date();
    const curMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setSelectedMonth(curMonth);
    setWeekOffset(0);

    const curTodayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const todayLogs = logsByDate[curTodayStr] || [];
    setActiveDayDetails({
      dateStr: curTodayStr,
      dayNumber: now.getDate(),
      totalCount: todayLogs.length,
      logs: todayLogs,
      isToday: true,
    });
  };
  const jumpToMonth = (year, monthIndex) => setSelectedMonth(new Date(year, monthIndex, 1));

  const monthLabel = useMemo(() => {
    return selectedMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [selectedMonth]);

  // Check if current viewed month has 0 records but other months have records
  const currentMonthKey = `${selectedMonth.getFullYear()}-${selectedMonth.getMonth()}`;
  const currentMonthHasNoRecordsForTier = mainTierFilter !== "all" && tierScopedTotalCount > 0 && !tierActiveMonths.includes(currentMonthKey);

  // Monthly Calendar Matrix (Days 1 to 31)
  const monthCalendarDays = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDayIndex = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0
    const totalDays = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDayIndex; i++) {
      days.push({ empty: true, key: `pad-${i}` });
    }
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayLogs = logsByDate[dateStr] || [];
      const count = dayLogs.length;
      const isToday = dateStr === todayStr;
      const isFuture = dateStr > todayStr;

      days.push({
        empty: false,
        day,
        dateStr,
        count,
        logs: dayLogs,
        isToday,
        isFuture,
        key: dateStr,
      });
    }
    return days;
  }, [selectedMonth, logsByDate, todayStr]);

  // Current Week calculation with EXACT 7 dates (Mon-Sun)
  const weekDays = useMemo(() => {
    const now = new Date();
    const dayOfWeek = (now.getDay() + 6) % 7; // Monday = 0
    const baseMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
    baseMonday.setDate(baseMonday.getDate() + weekOffset * 7);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(baseMonday);
      d.setDate(baseMonday.getDate() + i);

      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dt = String(d.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${dt}`;

      const dayLogs = logsByDate[dateStr] || [];
      const isToday = dateStr === todayStr;
      const isFuture = dateStr > todayStr;

      days.push({
        name: WEEKDAY_NAMES[i],
        dayNumber: d.getDate(),
        monthShort: d.toLocaleDateString("en-US", { month: "short" }),
        dateStr,
        logs: dayLogs,
        totalCheckins: dayLogs.length,
        isToday,
        isFuture,
      });
    }
    return days;
  }, [weekOffset, logsByDate, todayStr]);

  const weekRangeLabel = useMemo(() => {
    if (weekDays.length < 7) return "This Week";
    const start = `${weekDays[0].monthShort} ${weekDays[0].dayNumber}`;
    const endYear = new Date().getFullYear();
    const end = `${weekDays[6].monthShort} ${weekDays[6].dayNumber}, ${endYear}`;
    return `${start} – ${end}${weekOffset === 0 ? " (Current Week)" : ""}`;
  }, [weekDays, weekOffset]);

  // Deduplicated & Filtered Day Modal Attendees
  const modalAttendees = useMemo(() => {
    if (!activeDayDetails?.logs) return [];
    let list = activeDayDetails.logs;

    // Filter by modal type tab (Studio vs Live Session)
    if (modalTypeTab === "class") {
      list = list.filter((l) => l.type === "In-Studio Check-in" || l.type === "Class Check-in");
    } else if (modalTypeTab === "session") {
      list = list.filter((l) => l.type === "Live Session Check-in" || l.type === "Live Session");
    }

    // Filter by modal tier dropdown
    if (modalTierFilter !== "all") {
      list = list.filter((l) => isTierMatch(l.userTier, modalTierFilter));
    }

    // Deduplicate by User ID so single user does not have redundant duplicates
    const dedupeMap = new Map();
    list.forEach((log) => {
      const key = `${log.userId || log.userName}-${log.type}`;
      if (!dedupeMap.has(key)) {
        dedupeMap.set(key, { ...log, sessionTimes: [log.time] });
      } else {
        const existing = dedupeMap.get(key);
        if (!existing.sessionTimes.includes(log.time)) {
          existing.sessionTimes.push(log.time);
        }
      }
    });

    return Array.from(dedupeMap.values());
  }, [activeDayDetails, modalTypeTab, modalTierFilter]);

  const modalStudioCount = useMemo(() => {
    if (!activeDayDetails?.logs) return 0;
    return activeDayDetails.logs.filter((l) => l.type === "In-Studio Check-in" || l.type === "Class Check-in").length;
  }, [activeDayDetails]);

  const modalSessionCount = useMemo(() => {
    if (!activeDayDetails?.logs) return 0;
    return activeDayDetails.logs.filter((l) => l.type === "Live Session Check-in" || l.type === "Live Session").length;
  }, [activeDayDetails]);

  return (
    <div className="w-full">
      {/* ─── METRIC COUNT CARDS & TYPE SELECTOR ────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <motion.div
          whileHover={{ scale: 1.02 }}
          onClick={() => setTypeFilter("all")}
          className={`p-4 rounded-[20px] border cursor-pointer transition-all flex items-center justify-between ${
            typeFilter === "all"
              ? "bg-amber-500/10 border-amber-500 shadow-xs"
              : "bg-white/80 border-zinc-200/80 hover:bg-zinc-50"
          }`}
        >
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-[14px] bg-amber-500/20 text-amber-800 flex items-center justify-center font-bold">
              <FiLayers size={18} />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-wider text-zinc-400">Total Check-Ins</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <p className="text-[22px] font-black text-zinc-900 font-poppins">{tierScopedTotalCount}</p>
                <span className="text-[11px] font-extrabold text-amber-800 bg-amber-100/80 px-2 py-0.5 rounded-md">
                  {tierScopedUniqueUserCount} Unique {tierScopedUniqueUserCount === 1 ? "User" : "Users"}
                </span>
              </div>
            </div>
          </div>
          {typeFilter === "all" && <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-amber-500 text-white">Active</span>}
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          onClick={() => setTypeFilter("class")}
          className={`p-4 rounded-[20px] border cursor-pointer transition-all flex items-center justify-between ${
            typeFilter === "class"
              ? "bg-emerald-500/10 border-emerald-500 shadow-xs"
              : "bg-white/80 border-zinc-200/80 hover:bg-zinc-50"
          }`}
        >
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-[14px] bg-emerald-500/20 text-emerald-800 flex items-center justify-center font-bold">
              <FiUserCheck size={18} />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-wider text-zinc-400">In-Studio Class Check-Ins</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <p className="text-[22px] font-black text-emerald-800 font-poppins">{tierScopedUserClassCount}</p>
                <span className="text-[11px] font-extrabold text-emerald-800 bg-emerald-100/80 px-2 py-0.5 rounded-md">
                  {tierScopedStudioUniqueUserCount} Unique {tierScopedStudioUniqueUserCount === 1 ? "User" : "Users"}
                </span>
              </div>
            </div>
          </div>
          {typeFilter === "class" && <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-emerald-600 text-white">Active</span>}
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          onClick={() => setTypeFilter("session")}
          className={`p-4 rounded-[20px] border cursor-pointer transition-all flex items-center justify-between ${
            typeFilter === "session"
              ? "bg-purple-500/10 border-purple-500 shadow-xs"
              : "bg-white/80 border-zinc-200/80 hover:bg-zinc-50"
          }`}
        >
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-[14px] bg-purple-500/20 text-purple-800 flex items-center justify-center font-bold">
              <FiTv size={18} />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-wider text-zinc-400">Live Session Check-Ins</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <p className="text-[22px] font-black text-purple-800 font-poppins">{tierScopedLiveSessionCount}</p>
                <span className="text-[11px] font-extrabold text-purple-800 bg-purple-100/80 px-2 py-0.5 rounded-md">
                  {tierScopedLiveUniqueUserCount} Unique {tierScopedLiveUniqueUserCount === 1 ? "User" : "Users"}
                </span>
              </div>
            </div>
          </div>
          {typeFilter === "session" && <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-purple-600 text-white">Active</span>}
        </motion.div>
      </div>

      {/* ─── QUICK INTERACTIVE TIER PILLS BAR (SHOWING UNIQUE USERS!) ──────── */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-2.5 rounded-[22px] bg-zinc-100/90 border border-zinc-200/90">
        <span className="text-[11.5px] font-black uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 pl-2 pr-1">
          <FiFilter size={13} className="text-amber-600" /> Filter by Tier:
        </span>

        {heatmapTierOptions.map((opt) => {
          const isSelected = mainTierFilter === opt.value;
          return (
            <motion.button
              key={opt.value}
              whileTap={{ scale: 0.96 }}
              onClick={() => handleSelectMainTier(opt.value)}
              className={`px-3.5 py-1.5 rounded-full text-[11.5px] font-bold flex items-center gap-1.5 border cursor-pointer transition-all ${
                isSelected
                  ? "bg-zinc-900 text-white border-zinc-900 shadow-xs"
                  : "bg-white text-zinc-700 border-zinc-200/90 hover:bg-zinc-50"
              }`}
            >
              {opt.color && <span className="w-2 h-2 rounded-full" style={{ background: isSelected ? "#fff" : opt.color }} />}
              <span>{opt.shortLabel || opt.label}</span>
            </motion.button>
          );
        })}
      </div>

      {/* ─── TOP TOOLBAR: VIEW TOGGLES & MONTH CONTROLS ─────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 pb-4 border-b border-zinc-200/70">
        <div className="flex flex-wrap items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex gap-1 p-1 rounded-[16px] bg-zinc-100 border border-zinc-200/80 shadow-2xs">
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-3.5 py-1.5 rounded-[12px] text-[12px] font-bold flex items-center gap-1.5 border-none cursor-pointer transition-all ${
                viewMode === "calendar"
                  ? "bg-white text-zinc-900 shadow-xs"
                  : "bg-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <FiCalendar size={13} className={viewMode === "calendar" ? "text-amber-600" : ""} />
              Monthly Calendar (1–31)
            </button>

            <button
              onClick={() => setViewMode("weekly")}
              className={`px-3.5 py-1.5 rounded-[12px] text-[12px] font-bold flex items-center gap-1.5 border-none cursor-pointer transition-all ${
                viewMode === "weekly"
                  ? "bg-white text-zinc-900 shadow-xs"
                  : "bg-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <FiGrid size={13} className={viewMode === "weekly" ? "text-amber-600" : ""} />
              Real-Date Weekly Matrix
            </button>
          </div>

          {/* Navigation Controls */}
          {viewMode === "calendar" ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full border border-amber-900/15 shadow-2xs">
                <button
                  onClick={prevMonth}
                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-zinc-100 border-none bg-transparent cursor-pointer text-zinc-600"
                >
                  <FiChevronLeft size={14} />
                </button>
                <span className="text-[13px] font-black text-zinc-800 font-poppins min-w-[110px] text-center">
                  {monthLabel}
                </span>
                <button
                  onClick={nextMonth}
                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-zinc-100 border-none bg-transparent cursor-pointer text-zinc-600"
                >
                  <FiChevronRight size={14} />
                </button>
              </div>
              <button
                onClick={setMonthToday}
                className="px-3 py-1.5 rounded-full text-[11.5px] font-bold bg-amber-50 text-amber-900 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-all shadow-2xs"
              >
                Today
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full border border-amber-900/15 shadow-2xs">
                <button
                  onClick={() => setWeekOffset((w) => w - 1)}
                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-zinc-100 border-none bg-transparent cursor-pointer text-zinc-600"
                >
                  <FiChevronLeft size={14} />
                </button>
                <span className="text-[12.5px] font-black text-zinc-800 font-poppins min-w-[190px] text-center">
                  {weekRangeLabel}
                </span>
                <button
                  onClick={() => setWeekOffset((w) => w + 1)}
                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-zinc-100 border-none bg-transparent cursor-pointer text-zinc-600"
                >
                  <FiChevronRight size={14} />
                </button>
              </div>
              {weekOffset !== 0 && (
                <button
                  onClick={() => setWeekOffset(0)}
                  className="px-3 py-1.5 rounded-full text-[11.5px] font-bold bg-amber-50 text-amber-900 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-all shadow-2xs"
                >
                  Current Week
                </button>
              )}
            </div>
          )}
        </div>

        {/* Live Filter Indicator */}
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-zinc-500">Active Tier:</span>
          <span className="text-[12px] font-black px-3 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 capitalize">
            {mainTierFilter === "all" ? "All Tiers" : mainTierFilter} ({tierScopedUniqueUserCount} Unique User{tierScopedUniqueUserCount === 1 ? "" : "s"} · {tierScopedTotalCount} Check-Ins)
          </span>
          {mainTierFilter !== "all" && (
            <button
              onClick={() => handleSelectMainTier("all")}
              className="text-[11px] font-bold text-zinc-500 hover:text-zinc-900 underline cursor-pointer bg-transparent border-none"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* ─── HELPFUL AUTO-NAVIGATION BANNER IF CHECKINS EXIST IN ANOTHER MONTH ─ */}
      {currentMonthHasNoRecordsForTier && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3.5 mb-4 rounded-[18px] bg-amber-50 border border-amber-300 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-[13px] font-bold shrink-0">
              <FiInfo size={16} />
            </div>
            <p className="text-[12.5px] font-bold text-amber-950">
              No check-in records for <span className="font-extrabold capitalize">{mainTierFilter}</span> in {monthLabel}.{" "}
              <span className="text-zinc-600">({tierScopedUniqueUserCount} user(s) with {tierScopedTotalCount} check-in(s) located in July 2026)</span>
            </p>
          </div>
          <button
            onClick={() => jumpToMonth(2026, 6)} // July 2026
            className="px-4 py-1.5 rounded-full text-[12px] font-black bg-amber-600 text-white border-none cursor-pointer hover:bg-amber-700 flex items-center gap-1.5 shadow-xs transition-all shrink-0"
          >
            Jump to July 2026 <FiArrowRight size={13} />
          </button>
        </motion.div>
      )}

      {/* ─── VIEW 1: MONTHLY CALENDAR GRID (DAYS 1 TO 31) ────────────────────── */}
      {viewMode === "calendar" && (
        <div>
          {/* Day of Week Headers */}
          <div className="grid grid-cols-7 gap-2.5 mb-2.5">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => (
              <div key={d} className="text-center text-[11.5px] font-black uppercase tracking-[0.8px] text-zinc-400 py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid Tiles */}
          <div className="grid grid-cols-7 gap-2.5">
            {monthCalendarDays.map((cell) => {
              if (cell.empty) {
                return (
                  <div
                    key={cell.key}
                    className="h-24 rounded-[16px] bg-zinc-50/50 border border-dashed border-zinc-200/50"
                  />
                );
              }

              const hasCheckIn = cell.count > 0;
              const isSelected = activeDayDetails?.dateStr === cell.dateStr;

              return (
                <motion.div
                  key={cell.key}
                  whileHover={{ scale: 1.03, y: -2, zIndex: 25 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => cell.count > 0 && setActiveDayDetails(cell)}
                  className={`h-24 rounded-[18px] p-2.5 border flex flex-col justify-between cursor-pointer transition-all relative overflow-hidden ${
                    cell.isToday
                      ? "ring-2 ring-amber-500 shadow-md"
                      : isSelected
                      ? "ring-2 ring-emerald-500 shadow-md"
                      : ""
                  }`}
                  style={{
                    background: hasCheckIn
                      ? cell.count >= 2
                        ? "linear-gradient(145deg, #FFF6EA, #FFEDD5)"
                        : "linear-gradient(145deg, #F4F8F1, #E8F2E2)"
                      : cell.isFuture
                      ? "rgba(250,250,250,0.65)"
                      : "rgba(255,255,255,0.92)",
                    borderColor: hasCheckIn
                      ? cell.count >= 2
                        ? "rgba(242,154,12,0.45)"
                        : "rgba(107,138,90,0.45)"
                      : "rgba(239,229,216,0.85)",
                    boxShadow: hasCheckIn ? "0 4px 14px rgba(0,0,0,0.04)" : "none",
                  }}
                >
                  {/* Tile Header: Date Number & Today/Upcoming Pill */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-[13px] font-black font-poppins ${
                        cell.isToday
                          ? "text-amber-700 font-extrabold"
                          : hasCheckIn
                          ? "text-zinc-900"
                          : "text-zinc-600"
                      }`}
                    >
                      {cell.day}
                    </span>

                    {cell.isToday ? (
                      <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-amber-500 text-white shadow-2xs">
                        Today
                      </span>
                    ) : hasCheckIn ? (
                      <span
                        className="text-[9.5px] font-black px-2 py-0.5 rounded-full shadow-2xs"
                        style={{
                          background: cell.count >= 2 ? "linear-gradient(135deg,#F29A0C,#E97818)" : "linear-gradient(135deg,#6B8A5A,#8BA47A)",
                          color: "#fff",
                        }}
                      >
                        ✓ {cell.count} {cell.count === 1 ? "Check-in" : "Check-ins"}
                      </span>
                    ) : cell.isFuture ? (
                      <span className="text-[9px] font-bold text-zinc-300">Upcoming</span>
                    ) : null}
                  </div>

                  {/* Tile Body: Real Check-in Details */}
                  {hasCheckIn ? (
                    <div className="flex flex-col gap-0.5 mt-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[11px] font-extrabold text-zinc-900 truncate">
                          {cell.logs[0]?.userName || "Student"}
                        </p>
                        <span className="text-[8.5px] font-bold px-1.5 py-0.2 rounded-md bg-zinc-200 text-zinc-700 truncate">
                          {cell.logs[0]?.userTier}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-[9.5px] font-bold text-zinc-500 truncate">
                        <FiClock size={10} className="text-amber-600 shrink-0" />
                        <span>{cell.logs[0]?.time}</span>
                        {cell.count > 1 && <span className="text-amber-700 font-black">+{cell.count - 1} more</span>}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <span className="text-[10px] font-bold text-zinc-300">-</span>
                    </div>
                  )}

                  {/* Bottom Accent Line */}
                  {hasCheckIn && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-[3px]"
                      style={{
                        background: cell.count >= 2 ? `linear-gradient(90deg,${T.orange},${T.orange2})` : `linear-gradient(90deg,${T.olive},${T.olive2})`,
                      }}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── VIEW 2: REAL-DATE WEEKLY HOURLY SCHEDULE (NO FUTURE DATA LEAKAGE!) ─ */}
      {viewMode === "weekly" && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 800 }}>
            {/* Weekday Column Headers with EXACT Dates */}
            <div style={{ display: "grid", gridTemplateColumns: "75px repeat(7, 1fr)", gap: "8px" }} className="mb-2.5">
              <div className="text-[11px] font-black uppercase tracking-wider text-zinc-400 flex items-center justify-center">
                Time
              </div>
              {weekDays.map((d) => (
                <div
                  key={d.dateStr}
                  className={`p-2.5 rounded-[16px] border text-center transition-all ${
                    d.isToday
                      ? "bg-amber-500 text-white border-amber-600 shadow-md"
                      : d.totalCheckins > 0
                      ? "bg-emerald-50/80 border-emerald-300 text-zinc-900"
                      : "bg-white/90 border-zinc-200/80 text-zinc-600"
                  }`}
                >
                  <p className={`text-[11px] font-black uppercase tracking-wider ${d.isToday ? "text-amber-100" : "text-zinc-400"}`}>
                    {d.name}
                  </p>
                  <p className="text-[15px] font-black font-poppins mt-0.5">
                    {d.monthShort} {d.dayNumber}
                  </p>
                  <div className="mt-1">
                    {d.isToday ? (
                      <span className="text-[9px] font-black bg-white text-amber-700 px-2 py-0.5 rounded-full">
                        Today
                      </span>
                    ) : d.totalCheckins > 0 ? (
                      <span className="text-[9px] font-black bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                        {d.totalCheckins} Check-in{d.totalCheckins > 1 ? "s" : ""}
                      </span>
                    ) : d.isFuture ? (
                      <span className="text-[9px] font-bold text-zinc-400">Future</span>
                    ) : (
                      <span className="text-[9px] font-bold text-zinc-400">0 check-ins</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Hourly Grid Rows (6 AM through 8 PM) */}
            <div className="flex flex-col gap-1.5">
              {HOURLY_SLOTS.map((slot) => (
                <div key={slot.hour} style={{ display: "grid", gridTemplateColumns: "75px repeat(7, 1fr)", gap: "8px" }}>
                  {/* Hour Label */}
                  <div className="text-[11px] font-bold text-zinc-500 flex items-center justify-end pr-3">
                    {slot.label}
                  </div>

                  {/* 7 Daily Cells for this Specific Hour */}
                  {weekDays.map((d) => {
                    const matchingLogs = d.logs.filter((l) => l.hour === slot.hour);
                    const hasCheckins = matchingLogs.length > 0;

                    return (
                      <motion.div
                        key={d.dateStr}
                        whileHover={hasCheckins ? { scale: 1.08, zIndex: 30 } : {}}
                        onMouseEnter={() =>
                          hasCheckins &&
                          setHoveredInfo({
                            dateStr: d.dateStr,
                            dayName: d.name,
                            time: slot.label,
                            logs: matchingLogs,
                          })
                        }
                        onMouseLeave={() => setHoveredInfo(null)}
                        className={`h-9 rounded-[10px] border flex items-center justify-center transition-all ${
                          hasCheckins
                            ? "cursor-pointer shadow-sm"
                            : d.isFuture
                            ? "bg-zinc-50/40 border-dashed border-zinc-200/50"
                            : "bg-white/80 border-zinc-100"
                        }`}
                        style={{
                          background: hasCheckins
                            ? matchingLogs.length >= 2
                              ? "linear-gradient(135deg, #F29A0C, #E97818)"
                              : "linear-gradient(135deg, #6B8A5A, #8BA47A)"
                            : undefined,
                          borderColor: hasCheckins ? "transparent" : undefined,
                        }}
                      >
                        {hasCheckins ? (
                          <span className="text-[11px] font-black text-white drop-shadow-xs flex items-center gap-1">
                            <FiUserCheck size={11} /> {matchingLogs.length}
                          </span>
                        ) : d.isFuture ? (
                          <span className="text-[10px] font-bold text-zinc-300">-</span>
                        ) : null}
                      </motion.div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── INTERACTIVE HOVER BANNER ────────────────────────────────────────── */}
      {hoveredInfo && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 rounded-[16px] bg-amber-50 border border-amber-200 shadow-xs flex items-center justify-between"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-[12px]">
              <FiCheck />
            </div>
            <div>
              <p className="text-[12.5px] font-extrabold text-amber-900">
                {hoveredInfo.dayName}, {hoveredInfo.dateStr} at {hoveredInfo.time}
              </p>
              <p className="text-[11px] font-semibold text-zinc-600">
                {hoveredInfo.logs.map((l) => `${l.userName} (${l.userTier} • ${l.className} • ${l.location})`).join(", ")}
              </p>
            </div>
          </div>
          <span className="text-[11px] font-black px-2.5 py-1 rounded-full bg-amber-500 text-white">
            {hoveredInfo.logs.length} Student Check-in(s)
          </span>
        </motion.div>
      )}

      {/* ─── CLEAN, SPACIOUS DAY DETAILS INSPECTOR MODAL ─────────────────────── */}
      <AnimatePresence>
        {activeDayDetails && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveDayDetails(null)}
              className="fixed inset-0 bg-black/45 backdrop-blur-xs z-[9990]"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              className="ad-modal ad-pad ad-modal-scroll fixed inset-0 m-auto w-[94%] max-w-2xl h-fit max-h-[85vh] bg-white rounded-[32px] p-7 border border-amber-900/15 shadow-2xl z-[9995] flex flex-col justify-between"
            >
              {/* Modal Top Header */}
              <div>
                <div className="flex items-start justify-between pb-3.5 border-b border-zinc-100 mb-4">
                  <div>
                    <span className="text-[11px] font-black uppercase tracking-wider text-amber-800 bg-amber-100/90 px-3 py-0.5 rounded-full">
                      Daily Attendance Inspector
                    </span>
                    <h3 className="font-black text-[24px] text-zinc-900 font-poppins mt-1">
                      {activeDayDetails.dateStr}
                    </h3>
                  </div>
                  <button
                    onClick={() => setActiveDayDetails(null)}
                    className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200 border-none cursor-pointer transition-all"
                  >
                    <FiX size={18} />
                  </button>
                </div>

                {/* Attendance Category Tabs & Modal Tier Filter Bar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 p-2.5 rounded-[22px] bg-zinc-100/80 border border-zinc-200/80">
                  {/* Type Tab Selector */}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setModalTypeTab("all")}
                      className={`px-3.5 py-1.5 rounded-[14px] text-[12px] font-bold border-none cursor-pointer transition-all ${
                        modalTypeTab === "all"
                          ? "bg-white text-zinc-900 shadow-xs"
                          : "bg-transparent text-zinc-500 hover:text-zinc-800"
                      }`}
                    >
                      All ({activeDayDetails.count})
                    </button>

                    <button
                      onClick={() => setModalTypeTab("class")}
                      className={`px-3.5 py-1.5 rounded-[14px] text-[12px] font-bold border-none cursor-pointer flex items-center gap-1.5 transition-all ${
                        modalTypeTab === "class"
                          ? "bg-emerald-600 text-white shadow-xs"
                          : "bg-transparent text-zinc-500 hover:text-zinc-800"
                      }`}
                    >
                      <FiUserCheck size={13} /> In-Studio ({modalStudioCount})
                    </button>

                    <button
                      onClick={() => setModalTypeTab("session")}
                      className={`px-3.5 py-1.5 rounded-[14px] text-[12px] font-bold border-none cursor-pointer flex items-center gap-1.5 transition-all ${
                        modalTypeTab === "session"
                          ? "bg-purple-600 text-white shadow-xs"
                          : "bg-transparent text-zinc-500 hover:text-zinc-800"
                      }`}
                    >
                      <FiTv size={13} /> Live Session ({modalSessionCount})
                    </button>
                  </div>

                  {/* Modal Tier Filter Dropdown */}
                  <CustomDropdown
                    value={modalTierFilter}
                    options={heatmapTierOptions}
                    onChange={(val) => setModalTierFilter(val)}
                    placeholder="Filter Tier"
                    width="w-48"
                  />
                </div>
              </div>

              {/* Deduplicated Attendee Cards List (Scrollable Area) */}
              <div className="flex flex-col gap-3 my-2 overflow-y-auto max-h-[44vh] pr-1.5">
                {modalAttendees.length > 0 ? (
                  modalAttendees.map((log, idx) => {
                    const tierTheme = TIER_THEMES[(log.userTier || "standard").toLowerCase()] || TIER_THEMES["standard"];
                    const isStudio = log.type === "In-Studio Check-in" || log.type === "Class Check-in";

                    return (
                      <motion.div
                        key={log.id || idx}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-[22px] border border-zinc-200/90 bg-zinc-50/70 hover:bg-white transition-all shadow-2xs"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3.5 min-w-0">
                            {/* User Avatar Initials */}
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center font-black text-[15px] shrink-0 font-poppins shadow-2xs"
                              style={{ background: tierTheme.bg, color: tierTheme.text, border: `1px solid ${tierTheme.color}40` }}
                            >
                              {log.userName?.slice(0, 2).toUpperCase() || "ST"}
                            </div>

                            <div className="min-w-0">
                              {/* Name & Tier Badge */}
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-[14.5px] font-bold text-zinc-900 truncate">{log.userName}</p>
                                <span
                                  className="text-[10.5px] font-extrabold px-2.5 py-0.5 rounded-full"
                                  style={{ background: tierTheme.bg, color: tierTheme.text, border: `1px solid ${tierTheme.color}35` }}
                                >
                                  {log.userTier || "Standard"}
                                </span>
                              </div>

                              {/* Class & Location */}
                              <p className="text-[12px] font-semibold text-zinc-600 mt-1 flex items-center gap-1.5 truncate">
                                {isStudio ? <FiUserCheck className="text-emerald-600 shrink-0" /> : <FiTv className="text-purple-600 shrink-0" />}
                                <span>{log.className}</span>
                              </p>

                              <p className="text-[11px] font-semibold text-zinc-400 mt-0.5 flex items-center gap-1.5 truncate">
                                <FiMapPin size={11} className="text-amber-600 shrink-0" /> {log.location}
                              </p>
                            </div>
                          </div>

                          {/* Check-In Timestamps & Status */}
                          <div className="text-right shrink-0">
                            <div className="flex flex-col gap-1 items-end">
                              {log.sessionTimes?.map((t, ti) => (
                                <span key={ti} className="text-[12.5px] font-black text-zinc-900 font-poppins bg-white px-2.5 py-0.5 rounded-lg border border-zinc-200 shadow-2xs">
                                  {t}
                                </span>
                              ))}
                            </div>

                            <span
                              className={`text-[10px] font-black px-2.5 py-0.5 rounded-full mt-2 inline-block ${
                                isStudio ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-purple-100 text-purple-800 border border-purple-300"
                              }`}
                            >
                              {isStudio ? "Studio Present" : "Virtual Live"}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="py-10 text-center text-[13px] font-semibold text-zinc-400">
                    No attendees match the selected tier filter for this day.
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between">
                <span className="text-[12px] font-bold text-zinc-500">
                  {modalAttendees.length} Unique Student Attendee{modalAttendees.length === 1 ? "" : "s"}
                </span>
                <button
                  onClick={() => setActiveDayDetails(null)}
                  className="px-6 py-2.5 rounded-full text-[12.5px] font-bold bg-zinc-900 text-white border-none cursor-pointer hover:bg-zinc-800 shadow-xs transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── MAIN REPORTS & ANALYTICS COMPONENT ───────────────────────────────────────
export default function ReportsAnalyticsPage() {
  const { packages: dynamicPackages = [], tiers: dynamicTiers = [] } = useDynamicPackages();
  const [range, setRange] = useState("12m");
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [toast, setToast] = useState(null);

  // Set of strictly active tier canonical names
  const activeTierSet = useMemo(() => {
    const set = new Set(["free trial", "free"]);
    (dynamicTiers || []).forEach((t) => {
      const name = typeof t === "string" ? t : (t.name || t.packageType || String(t));
      if (name) {
        set.add(name.trim().toLowerCase());
        set.add(packageService.normalizeTierName(name).toLowerCase());
      }
    });
    return set;
  }, [dynamicTiers]);

  // Table State: Search, Custom Tier Filter, Pagination
  const [tableSearch, setTableSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default to 10 rows
  const [hoveredMapPin, setHoveredMapPin] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3200);
  };

  const RANGES = ["7d", "30d", "90d", "12m"];
  const RANGE_LABELS = {
    "7d": "Last 7 Days",
    "30d": "Last 30 Days",
    "90d": "Last 90 Days",
    "12m": "Last 12 Months",
  };

  // Fetch live analytics
  const loadAnalytics = async (selectedRange) => {
    try {
      setLoading(true);
      const res = await adminApi.getAnalytics(selectedRange).catch(() => null);
      if (res?.success && res.data) {
        setAnalyticsData(res.data);
      }
    } catch (err) {
      console.warn("Failed to load analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics(range);
  }, [range]);

  // No real data yet → show zeros, never placeholder numbers.
  const kpi = useMemo(() => ({
    totalRev: 0,
    revGrowth: "0%",
    revUp: true,
    activeUsers: 0,
    usersGrowth: "0",
    usersUp: true,
    sessions: 0,
    sessionsGrowth: "0",
    sessUp: true,
    engagement: "0%",
    engGrowth: "0%",
    engUp: true,
    churn: "0%",
    churnGrowth: "0%",
    churnUp: false,
    totalUsers: 0,
    rangeNewUsers: 0,
    ...(analyticsData?.kpi || {}),
  }), [analyticsData]);

  const charts = (() => {
    const labels = analyticsData?.charts?.labels?.length
      ? analyticsData.charts.labels
      : Array.from({ length: 12 }, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - 11 + i);
          return d.toLocaleString("en-US", { month: "short" });
        });
    const zeros = labels.map(() => 0);
    return {
      labels,
      revenue: analyticsData?.charts?.revenue?.length ? analyticsData.charts.revenue : zeros,
      users: analyticsData?.charts?.users?.length ? analyticsData.charts.users : zeros,
    };
  })();

  // Deduplicate and normalize dynamic tiers strictly for active packages
  const tierDist = useMemo(() => {
    const rawList = analyticsData?.tierDistribution;

    let listToUse = [];

    if (Array.isArray(rawList) && rawList.length > 0) {
      // Filter out any deleted/inactive packages
      listToUse = rawList.filter((t) => {
        const canonical = packageService.normalizeTierName(t.tier).toLowerCase();
        return activeTierSet.has(canonical) || canonical.includes("trial") || canonical === "free";
      });
    }

    // If no backend distribution yet, fallback strictly to dynamic active tiers
    if (listToUse.length === 0) {
      const activeList = (dynamicTiers && dynamicTiers.length > 0) ? dynamicTiers : ["Free Trial", "Basic", "Standard", "Premium"];
      listToUse = activeList.map((t) => {
        const canonical = packageService.normalizeTierName(typeof t === "string" ? t : (t.name || t.packageType || String(t)));
        return { tier: canonical, n: 0, pct: 0 };
      });
    }

    const totalSum = listToUse.reduce((acc, curr) => acc + (curr.n || 0), 0);

    const dedupeMap = new Map();
    listToUse.forEach((t) => {
      const canonical = packageService.normalizeTierName(t.tier);
      if (!dedupeMap.has(canonical.toLowerCase())) {
        const theme = TIER_THEMES[canonical.toLowerCase()] || TIER_THEMES["standard"] || { color: "#6B8A5A", grad: "" };
        const n = t.n || 0;
        const pct = totalSum > 0 ? Math.round((n / totalSum) * 100) : (t.pct || 0);
        dedupeMap.set(canonical.toLowerCase(), {
          ...t,
          n,
          pct,
          tier: canonical,
          color: theme.color,
          grad: theme.grad,
        });
      }
    });

    return Array.from(dedupeMap.values());
  }, [analyticsData, activeTierSet, dynamicTiers]);

  // Dynamic Tier Options for Content Table Custom Dropdowns
  const dynamicTierDropdownOptions = useMemo(() => {
    const opts = [{ label: "All Tiers", value: "all" }];
    tierDist.forEach((t) => {
      opts.push({
        label: t.tier,
        value: t.tier.toLowerCase(),
        color: t.color,
      });
    });
    return opts;
  }, [tierDist]);

  const topCountries = analyticsData?.topCountries || [];

  const retention = {
    retentionRate: "0%",
    retentionPct: 0,
    renewalRate: "0%",
    renewalPct: 0,
    churnRate: "0%",
    churnPct: 0,
    avgLtv: "₹0",
    avgLtvPct: 0,
    ...(analyticsData?.retention || {}),
  };
  const activeAccounts = Number(kpi.activeUsers) || 0;
  const totalAccounts = Number(kpi.totalUsers) || 0;
  const expiredAccounts = Number(retention.expiredCount ?? Math.max(0, totalAccounts - activeAccounts)) || 0;

  // Heatmap Matrix
  const heatmapMatrix = analyticsData?.heatmap?.matrix || [];

  // Top Attendance Days
  const topAttendanceDays = analyticsData?.heatmap?.topDays || [];

  // Peak Hours Slot & Activity Info
  const peakSlotInfo = analyticsData?.heatmap?.peakSlot || "";

  // Insight cards — derived only from real data (zeros when nothing yet)
  const insights = useMemo(() => {
    const paidTiers = tierDist.filter((t) => !t.tier.toLowerCase().includes("free") && !t.tier.toLowerCase().includes("trial"));
    const paidTotal = paidTiers.reduce((a, t) => a + (t.n || 0), 0);
    const leader = paidTiers.reduce((best, t) => ((t.n || 0) > (best?.n || 0) ? t : best), null);
    const leaderPct = leader && paidTotal > 0 ? Math.round(((leader.n || 0) / paidTotal) * 1000) / 10 : 0;
    const activePct = totalAccounts > 0 ? Math.round((activeAccounts / totalAccounts) * 1000) / 10 : 0;
    return { leader, leaderPct, activePct };
  }, [tierDist, activeAccounts, totalAccounts]);

  // Dynamic Average LTV & Health Rate
  const retentionHealth = useMemo(() => {
    return {
      retentionRate: analyticsData?.retentionRate ?? 0,
      churnRate: analyticsData?.churnRate ?? 0,
      avgLtv: analyticsData?.avgLtv || (kpi.totalRev ? Math.round(kpi.totalRev / Math.max(kpi.activeUsers, 1)) : 0),
    };
  }, [analyticsData, kpi]);

  // Calendar Check-in History Logs
  const calendarLogs = analyticsData?.calendarLogs || [];

  // Live database practice videos list
  const allContentVideos = useMemo(() => {
    return (analyticsData?.topContent && Array.isArray(analyticsData.topContent) && analyticsData.topContent.length > 0)
      ? analyticsData.topContent
      : [];
  }, [analyticsData]);

  // Filtered Content
  const filteredContent = useMemo(() => {
    return allContentVideos.filter((v) => {
      const matchSearch =
        (v.title || "").toLowerCase().includes(tableSearch.toLowerCase()) ||
        (v.category || "").toLowerCase().includes(tableSearch.toLowerCase());
      const matchTier = tierFilter === "all" || (v.tier || "").toLowerCase().includes(tierFilter.toLowerCase());
      return matchSearch && matchTier;
    });
  }, [allContentVideos, tableSearch, tierFilter]);

  const effectivePageSize = pageSize === "all" ? filteredContent.length || 1 : Number(pageSize);
  const totalPages = Math.ceil(filteredContent.length / effectivePageSize) || 1;

  const pageSizeOptions = useMemo(() => [
    { label: "Show 5", value: 5 },
    { label: "Show 10", value: 10 },
    { label: "Show 20", value: 20 },
    { label: `Show All (${allContentVideos.length})`, value: "all" },
  ], [allContentVideos.length]);

  const paginatedContent = useMemo(() => {
    if (pageSize === "all") return filteredContent;
    const start = (currentPage - 1) * effectivePageSize;
    return filteredContent.slice(start, start + effectivePageSize);
  }, [filteredContent, currentPage, effectivePageSize, pageSize]);

  const handleExportContentReport = () => {
    try {
      const headers = ["Rank", "Video Title", "Category", "Access Tier", "Duration"];
      const rows = filteredContent.map((c, i) => [
        i + 1,
        `"${c.title}"`,
        `"${c.category}"`,
        `"${c.tier}"`,
        `"${c.duration || "30 mins"}"`,
      ]);

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `DivyaYoga_Content_Performance_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("Content Performance CSV downloaded!");
    } catch (e) {
      showToast("Failed to generate CSV export");
    }
  };

  // CSV Export Handler
  const handleExportExecutiveReport = () => {
    try {
      const summaryRows = [
        ["DIVYYOG EXECUTIVE ANALYTICS REPORT"],
        ["Timeframe Range", RANGE_LABELS[range] || range],
        ["Generated At", new Date().toLocaleString("en-IN")],
        [],
        ["METRIC", "VALUE", "GROWTH / STATUS"],
        [range === "12m" ? "Total Revenue" : "Range Revenue", `₹${Number(kpi.totalRev || 0).toLocaleString()}`, kpi.revGrowth],
        ["Active Accounts in Range", kpi.activeUsers, kpi.usersGrowth],
        ["Total Platform Users", kpi.totalUsers, "Registered"],
        ["Sessions Conducted", kpi.sessions, kpi.sessionsGrowth],
        ["Average Engagement", kpi.engagement, kpi.engGrowth],
        ["Platform Churn Rate", kpi.churn, kpi.churnGrowth],
        [],
        ["SUBSCRIPTION TIER BREAKDOWN"],
        ["Tier Name", "Active Members", "Share %"],
        ...tierDist.map((t) => [t.tier, t.n, `${t.pct}%`]),
        [],
        ["TOP MEMBER LOCATIONS"],
        ["Country / Region", "Members", "Share %"],
        ...topCountries.map((c) => [c.name, c.users, `${c.pct}%`]),
      ];

      const csvContent = "data:text/csv;charset=utf-8," + summaryRows.map((e) => e.map((cell) => `"${cell}"`).join(",")).join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Divyayoga_Analytics_Report_${range}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("Executive Analytics Report downloaded as CSV");
    } catch (err) {
      console.error(err);
      showToast("Failed to generate CSV export");
    }
  };

  if (loading && !analyticsData) {
    return (
      <AdminDashboardLayout activeNav="analytics">
        <BrandLoader 
          message="Loading Reports & Analytics..." 
          subMessage="Compiling platform metrics, revenue, and retention reports" 
        />
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout activeNav="analytics">
      <div style={{ fontFamily: "'Inter',sans-serif" }}>
        <div className="relative z-10 w-full min-w-0">
        {/* Ambient background orbs */}
        <motion.div
          animate={{ y: [0, -18, 0], x: [0, 14, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter: "blur(80px)", top: -140, right: -80, zIndex: 0 }}
        />
        <motion.div
          animate={{ y: [0, 14, 0], x: [0, -10, 0] }}
          transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
          className="fixed w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle,rgba(242,154,12,0.06),transparent 70%)", filter: "blur(80px)", bottom: -80, left: 40, zIndex: 0 }}
        />

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* TOPBAR WITH DATE TIMEFRAME & EXPORT                               */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 28, letterSpacing: "-1px" }}>
                Reports & Analytics
              </h1>
              <span className="text-[11px] font-black text-amber-800 bg-amber-100/90 px-3 py-0.5 rounded-full border border-amber-300 shadow-2xs">
                ● LIVE
              </span>
            </div>
            <p className="text-[13px] mt-1 font-semibold text-zinc-500">
              {RANGE_LABELS[range]} · Comprehensive platform growth, retention & class metrics
            </p>
          </div>

          <div className="ad-wrap flex items-center gap-3">
            {/* Range Selector */}
            <div className="ad-scroll flex gap-1 p-1.5 rounded-[18px] bg-white/90 border border-amber-900/10 shadow-xs">
              {RANGES.map((r) => (
                <motion.button
                  key={r}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setRange(r)}
                  className="px-4 py-1.5 rounded-[13px] text-[12px] font-bold border-none cursor-pointer transition-all"
                  style={
                    range === r
                      ? { background: `linear-gradient(135deg,${T.olive},${T.olive2})`, color: "#fff", boxShadow: "0 4px 12px rgba(107,138,90,0.35)" }
                      : { background: "transparent", color: T.muted }
                  }
                >
                  {r}
                </motion.button>
              ))}
            </div>

            {/* Export Report Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleExportExecutiveReport}
              className="h-10 px-5 rounded-full text-[12px] font-bold flex items-center gap-2 text-white border-none cursor-pointer shadow-md"
              style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow: "0 5px 16px rgba(242,154,12,0.38)" }}
            >
              <FiDownload size={14} /> Export Report
            </motion.button>
          </div>
        </motion.div>

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 1: TOP 5 KPI SUMMARY STRIP (SPACIOUS & BALANCED)          */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <AnimatePresence mode="wait">
          <motion.div
            key={range}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="ad-kpi grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6"
          >
            <StatCard
              icon={FiDollarSign}
              value={`₹${Number(kpi.totalRev || 0).toLocaleString()}`}
              label={range === "12m" ? "Total Revenue" : "Range Revenue"}
              trend={kpi.revGrowth}
              trendUp={kpi.revUp}
              iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
              accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
              cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)"
              delay={0.04}
            />

            <StatCard
              icon={FiUsers}
              value={kpi.activeUsers.toString()}
              label={range === "12m" ? "Active Accounts" : "Active in Period"}
              trend={kpi.usersGrowth}
              trendUp={kpi.usersUp}
              iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
              accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
              cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)"
              delay={0.08}
            />

            <StatCard
              icon={FiVideo}
              value={kpi.sessions.toString()}
              label="Sessions Conducted"
              trend={kpi.sessionsGrowth}
              trendUp={kpi.sessUp}
              iconBg="rgba(83,74,183,0.2)"
              accentColor={T.purple}
              delay={0.12}
            />

            <StatCard
              icon={FiActivity}
              value={kpi.engagement}
              label="Avg Engagement"
              trend={kpi.engGrowth}
              trendUp={kpi.engUp}
              iconBg="rgba(34,197,94,0.2)"
              accentColor={T.green}
              delay={0.16}
            />

            <StatCard
              icon={FiTrendingDown}
              value={kpi.churn}
              label={range === "12m" ? "Churn / Expired" : "Period Churn"}
              trend={kpi.churnGrowth}
              trendUp={!kpi.churnUp}
              positive={false}
              iconBg="rgba(226,75,74,0.15)"
              accentColor={T.red}
              cardBg="linear-gradient(145deg,rgba(226,75,74,0.06),rgba(252,235,235,0.3))"
              delay={0.2}
            />
          </motion.div>
        </AnimatePresence>

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 2: CHARTS ROW 1 — REVENUE TREND & USER GROWTH             */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Trend Line Chart */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-[28px] p-6 flex flex-col justify-between" style={glass}>
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-zinc-100">
              <div>
                <p className="font-black text-[17px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                  Revenue Trend
                </p>
                <p className="text-[12px] mt-0.5 font-semibold text-zinc-400">{RANGE_LABELS[range]} · Hover points for exact date & amount</p>
              </div>
              <div className="text-right">
                <p className="font-black text-[20px] text-emerald-700 font-poppins">₹{Number(kpi.totalRev || 0).toLocaleString()}</p>
                <TrendBadge val={kpi.revGrowth} up={kpi.revUp} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={range} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <RevenueLineChart data={charts.revenue} labels={charts.labels} unit="₹" />
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* User Growth Bar Chart */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="rounded-[28px] p-6 flex flex-col justify-between" style={glass}>
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-zinc-100">
              <div>
                <p className="font-black text-[17px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                  User Growth & Registrations
                </p>
                <p className="text-[12px] mt-0.5 font-semibold text-zinc-400">{RANGE_LABELS[range]} · New student enrollments</p>
              </div>
              <div className="text-right">
                <p className="font-black text-[20px] text-[#6B8A5A] font-poppins">+{Number(kpi.rangeNewUsers) || 0} Users</p>
                <span className="text-[11px] font-bold text-zinc-400">Total: {totalAccounts} accounts</span>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={range} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <UserGrowthBarChart data={charts.users} labels={charts.labels} color={T.olive} />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 3: CHARTS ROW 2 — DONUT + COUNTRIES + RETENTION           */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 3: CHARTS ROW 2 — DONUT + REAL WORLD MAP + RETENTION      */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-6">
          {/* 1. Subscription Mix 3D Donut (3 Cols) */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="xl:col-span-3 rounded-[28px] p-6 flex flex-col justify-between" style={glass}>
            <div>
              <p className="font-black text-[17px] text-[#1F1F1F] mb-0.5" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                Subscription Mix
              </p>
              <p className="text-[12px] font-semibold text-zinc-400 mb-4">
                {(() => {
                  const paidCount = tierDist.filter((t) => !t.tier.toLowerCase().includes("free")).reduce((a, b) => a + (b.n || 0), 0);
                  const freeCount = tierDist.filter((t) => t.tier.toLowerCase().includes("free")).reduce((a, b) => a + (b.n || 0), 0);
                  return `${paidCount} Paid Subscriber${paidCount === 1 ? "" : "s"} · ${freeCount} Free Trial${freeCount === 1 ? "" : "s"}`;
                })()}
              </p>

              <div className="my-3">
                <SubscriptionDonutChart tiers={tierDist} totalCount={kpi.activeUsers} />
              </div>
            </div>

            <div className="flex flex-col gap-2.5 pt-4 border-t border-zinc-200/70">
              {tierDist.map((d) => (
                <div key={d.tier} className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ background: d.color }} />
                    <span className="text-[12.5px] font-bold text-zinc-700 truncate">{d.tier}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[12.5px] font-black text-zinc-900">{d.n} users</span>
                    <span className="text-[11.5px] font-bold text-zinc-400">({d.pct}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* 2. Top Countries & Global Reach — WIDE AUTHENTIC WORLD MAP (6 Cols) */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="xl:col-span-6 rounded-[28px] p-6 flex flex-col justify-between"
            style={{
              ...glass,
              background: "linear-gradient(160deg, rgba(255,255,255,0.95), rgba(255,249,242,0.9))",
            }}
          >
            <div>
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-black text-[17px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                      Top Countries & Global Reach
                    </p>
                    <span className="text-[9.5px] font-black text-amber-800 bg-amber-100/90 px-2.5 py-0.5 rounded-full border border-amber-300">
                      LIVE GEO SYNC
                    </span>
                  </div>
                  <p className="text-[11.5px] font-semibold text-zinc-400 mt-0.5">Real-time member geographical distribution & global presence</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center text-amber-600 shadow-2xs">
                  <FiGlobe size={16} />
                </div>
              </div>

              {/* Authentic World Map Canvas with Real SVG Coastlines & Precision Pinpoint */}
              <div className="relative w-full h-[285px] lg:h-[315px] rounded-[24px] bg-gradient-to-b from-amber-950/[0.04] via-amber-900/[0.02] to-zinc-900/[0.06] border border-amber-900/10 p-2 overflow-hidden my-3 flex items-center justify-center shadow-inner">
                {/* Background Authentic World Map SVG */}
                <svg viewBox="0 15 800 315" className="w-full h-full">
                  <defs>
                    <pattern id="grid-dots-world" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
                      <circle cx="4" cy="4" r="1.1" fill="#B45309" opacity="0.18" />
                    </pattern>
                    <linearGradient id="indiaGradReal" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.95" />
                      <stop offset="100%" stopColor="#D97706" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <rect x="0" y="15" width="800" height="315" fill="url(#grid-dots-world)" />

                  {/* All Sovereign World Continents & Nations */}
                  <path
                    d={WORLD_MAP_PATHS.worldPath}
                    fill="rgba(107,138,90,0.65)"
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="0.6"
                  />

                  {/* Highlighted Sovereign Indian Peninsula with Hover Trigger */}
                  <path
                    d={WORLD_MAP_PATHS.indiaPath}
                    fill="url(#indiaGradReal)"
                    stroke="#B45309"
                    strokeWidth={hoveredMapPin ? "2.2" : "1.2"}
                    className="cursor-pointer transition-all duration-300 filter drop-shadow-sm"
                    onMouseEnter={() => setHoveredMapPin(true)}
                    onMouseLeave={() => setHoveredMapPin(false)}
                  />

                  {/* Clean Precision Beacon on India (MidX: 584, MidY: 151) — Fully unobscured */}
                  <g
                    transform="translate(584, 151)"
                    className="cursor-pointer transition-all duration-300"
                    onMouseEnter={() => setHoveredMapPin(true)}
                    onMouseLeave={() => setHoveredMapPin(false)}
                  >
                    {/* Pulsating Beacon Rings */}
                    <circle r="14" fill="#F59E0B" opacity="0.35" className="animate-ping" />
                    <circle r="6.5" fill="#F59E0B" opacity="0.75" />
                    <circle r="3.5" fill="#D97706" stroke="#FFFFFF" strokeWidth="1.5" />

                    {/* Elevated Tooltip ONLY on Hover with pointer stalk (never covers the map) */}
                    {hoveredMapPin && (
                      <g transform="translate(0, -32)" className="transition-all duration-300">
                        {/* Pointer stalk down to the beacon */}
                        <line x1="0" y1="15" x2="0" y2="28" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="2 2" />

                        <rect
                          x="-115"
                          y="-15"
                          width="230"
                          height="30"
                          rx="15"
                          fill="#18181B"
                          stroke="rgba(245,158,11,0.85)"
                          strokeWidth="1.5"
                          filter="drop-shadow(0 12px 24px rgba(0,0,0,0.5))"
                        />
                        <text x="-100" y="5" fontSize="13" fontWeight="bold" fill="#FFFFFF">
                          🇮🇳
                        </text>
                        <text x="-78" y="5" fontSize="12" fontWeight="900" fill="#FBBF24" fontFamily="'Poppins', sans-serif">
                          India
                        </text>
                        <text x="-40" y="5" fontSize="11" fontWeight="700" fill="#FFFFFF" fontFamily="'Inter', sans-serif">
                          · {topCountries.find((c) => /india/i.test(c.name || ""))?.users ?? 0} Active Users
                        </text>
                        <text x="68" y="5" fontSize="10" fontWeight="800" fill="#A1A1AA" fontFamily="'Inter', sans-serif">
                          ({topCountries.find((c) => /india/i.test(c.name || ""))?.pct ?? 0}%)
                        </text>
                      </g>
                    )}
                  </g>
                </svg>

                {/* Ambient Coordinates tag in map corner */}
                <div className="absolute bottom-2.5 left-3.5 text-[9.5px] font-black font-mono text-zinc-500 bg-white/90 px-2.5 py-0.5 rounded-md border border-amber-900/10 backdrop-blur-xs shadow-2xs">
                  PRIMARY HUB: 12.9716° N, 77.5946° E (IND)
                </div>

                {/* Hover Hint in right corner */}
                <div className="absolute bottom-2.5 right-3.5 text-[9.5px] font-bold text-zinc-400 bg-white/80 px-2 py-0.5 rounded-md border border-zinc-200 backdrop-blur-xs">
                  {hoveredMapPin ? "● Viewing live student distribution" : "Hover country pin for details"}
                </div>
              </div>

              {/* Country Listing Bar */}
              <div className="flex flex-col gap-2.5 mt-1">
                {topCountries.map((c, i) => (
                  <div key={c.name} className="p-3 rounded-[18px] bg-white/90 border border-zinc-200/80 shadow-2xs flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <svg className="w-6 h-4 rounded-[3px] overflow-hidden shadow-2xs shrink-0 border border-zinc-200" viewBox="0 0 30 20">
                        <rect width="30" height="6.67" fill="#FF9933" />
                        <rect y="6.67" width="30" height="6.67" fill="#FFFFFF" />
                        <rect y="13.33" width="30" height="6.67" fill="#138808" />
                        <circle cx="15" cy="10" r="2.5" fill="none" stroke="#000080" strokeWidth="0.6" />
                        <circle cx="15" cy="10" r="0.6" fill="#000080" />
                      </svg>
                      <div className="min-w-0">
                        <p className="text-[13px] font-bold text-zinc-900 truncate">{c.name}</p>
                        <p className="text-[10.5px] font-semibold text-zinc-400 truncate">Primary Operating Hub (Bangalore, Karnataka & Pan-India)</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[13.5px] font-black text-zinc-900 font-poppins">{c.users} users</p>
                      <span className="text-[10px] font-black text-amber-700 bg-amber-100/90 px-2 py-0.5 rounded-full">
                        {c.pct}% Share
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-3.5 pt-2.5 border-t border-zinc-200/60 flex items-center justify-between text-[10.5px] font-bold text-zinc-400">
              <span className="flex items-center gap-1.5">
                <FiMapPin size={12} className="text-amber-600" />
                <span>Pan-India & Global Platform Access</span>
              </span>
              <span className="text-emerald-700 font-extrabold">100% Verified Accounts</span>
            </div>
          </motion.div>

          {/* 3. Retention & Health Overview — High-Tech Executive Health Hub (3 Cols) */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="xl:col-span-3 rounded-[28px] p-6 flex flex-col justify-between"
            style={{
              ...glass,
              background: "linear-gradient(160deg, rgba(255,255,255,0.95), rgba(248,250,246,0.9))",
            }}
          >
            <div>
              {/* Card Header */}
              <div className="flex items-center justify-between mb-3.5">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-black text-[17px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                      Retention & Churn
                    </p>
                    <span className="text-[9.5px] font-black text-emerald-800 bg-emerald-100/90 px-2 py-0.5 rounded-full border border-emerald-300">
                      HEALTH: EXCELLENT
                    </span>
                  </div>
                  <p className="text-[11.5px] font-semibold text-zinc-400 mt-0.5">Subscriber lifecycle & platform health</p>
                </div>

                <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-600 shadow-2xs">
                  <FiActivity size={16} />
                </div>
              </div>

              {/* Hero Retention Gauge Banner */}
              <div className="p-3.5 rounded-[20px] bg-gradient-to-r from-emerald-50 via-teal-50/60 to-amber-50/50 border border-emerald-200/80 mb-4 flex items-center justify-between shadow-2xs">
                <div className="flex items-center gap-3">
                  <div className="relative flex items-center justify-center">
                    {/* Circular mini gauge */}
                    <svg className="w-12 h-12 -rotate-90">
                      <circle cx="24" cy="24" r="19" stroke="rgba(107,138,90,0.2)" strokeWidth="4.5" fill="none" />
                      <motion.circle
                        cx="24" cy="24" r="19"
                        stroke="#16A34A" strokeWidth="4.5" fill="none"
                        strokeDasharray={2 * Math.PI * 19}
                        initial={{ strokeDashoffset: 2 * Math.PI * 19 }}
                        animate={{ strokeDashoffset: (2 * Math.PI * 19) * (1 - (Number(retention.retentionPct) || 0) / 100) }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="absolute text-[11px] font-black text-emerald-800 font-poppins">
                      {Math.round(Number(retention.retentionPct) || 0)}%
                    </span>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-emerald-800">Retention Score</p>
                    <p className="text-[13px] font-black text-zinc-900 font-poppins">{retention.retentionRate || "0%"}</p>
                    <p className="text-[9.5px] font-bold text-zinc-500">{activeAccounts} Active / {totalAccounts} Total Users</p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-[9.5px] font-black uppercase tracking-wider text-rose-700 bg-rose-100/90 px-2 py-0.5 rounded-full border border-rose-200">
                    Churn: {retention.churnRate || "0%"}
                  </span>
                  <p className="text-[9.5px] font-bold text-zinc-400 mt-1">{expiredAccounts} Expired Accounts</p>
                </div>
              </div>

              {/* 4 Interactive Bento Metric Tiles */}
              <div className="grid grid-cols-2 gap-2.5">
                {/* 1. Retention Rate */}
                <div className="p-3 rounded-[16px] bg-white/90 border border-zinc-200/80 shadow-2xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-400">Retention</span>
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  </div>
                  <p className="text-[16px] font-black text-emerald-800 font-poppins">{retention.retentionRate || "0%"}</p>
                  <div className="h-[3.5px] rounded-full overflow-hidden bg-zinc-100 my-1.5">
                    <motion.div
                      className="h-full rounded-full bg-emerald-600"
                      initial={{ width: 0 }}
                      animate={{ width: `${Number(retention.retentionPct) || 0}%` }}
                      transition={{ duration: 0.9 }}
                    />
                  </div>
                  <p className="text-[9.5px] font-bold text-zinc-500 truncate">Active unexpired</p>
                </div>

                {/* 2. Renewal Ratio */}
                <div className="p-3 rounded-[16px] bg-white/90 border border-zinc-200/80 shadow-2xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-400">Renewal</span>
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                  </div>
                  <p className="text-[16px] font-black text-amber-700 font-poppins">{retention.renewalRate || "0%"}</p>
                  <div className="h-[3.5px] rounded-full overflow-hidden bg-zinc-100 my-1.5">
                    <motion.div
                      className="h-full rounded-full bg-amber-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${Number(retention.renewalPct) || 0}%` }}
                      transition={{ duration: 0.9 }}
                    />
                  </div>
                  <p className="text-[9.5px] font-bold text-zinc-500 truncate">Target benchmark</p>
                </div>

                {/* 3. Churn Rate */}
                <div className="p-3 rounded-[16px] bg-white/90 border border-zinc-200/80 shadow-2xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-400">Churn Rate</span>
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                  </div>
                  <p className="text-[16px] font-black text-rose-700 font-poppins">{retention.churnRate || "0%"}</p>
                  <div className="h-[3.5px] rounded-full overflow-hidden bg-zinc-100 my-1.5">
                    <motion.div
                      className="h-full rounded-full bg-rose-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${Number(retention.churnPct) || 0}%` }}
                      transition={{ duration: 0.9 }}
                    />
                  </div>
                  <p className="text-[9.5px] font-bold text-zinc-500 truncate">Expired accounts</p>
                </div>

                {/* 4. Avg Customer LTV */}
                <div className="p-3 rounded-[16px] bg-white/90 border border-zinc-200/80 shadow-2xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-400">Avg LTV</span>
                    <span className="w-2 h-2 rounded-full bg-sky-500" />
                  </div>
                  <p className="text-[15px] font-black text-sky-800 font-poppins truncate">{retention.avgLtv || "₹0"}</p>
                  <div className="h-[3.5px] rounded-full overflow-hidden bg-zinc-100 my-1.5">
                    <motion.div
                      className="h-full rounded-full bg-sky-600"
                      initial={{ width: 0 }}
                      animate={{ width: `${Number(retention.avgLtvPct) || 0}%` }}
                      transition={{ duration: 0.9 }}
                    />
                  </div>
                  <p className="text-[9.5px] font-bold text-zinc-500 truncate">Revenue / paid user</p>
                </div>
              </div>
            </div>

            {/* Card Bottom Insight Tag */}
            <div className="mt-3.5 pt-2.5 border-t border-zinc-200/60 flex items-center justify-between text-[10.5px] font-bold text-zinc-500">
              <span className="flex items-center gap-1.5">
                <FiShield size={12} className="text-emerald-600" />
                <span>Real-time Subscriber Health</span>
              </span>
              <span className="text-emerald-700 font-extrabold">Top 10% Industry</span>
            </div>
          </motion.div>
        </div>

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 4: SAAS ATTENDANCE CALENDAR & REAL-DATE WEEKLY MATRIX      */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="rounded-[28px] p-6 mb-6" style={glass}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2.5">
                <p className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                  Session Attendance Calendar Suite
                </p>
                <span className="text-[10px] font-black text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full">
                  100% REAL LOGS
                </span>
              </div>
              <p className="text-[12px] font-semibold text-zinc-400 mt-0.5">
                Accurate day-by-day attendee tracking with zero future data leakage
              </p>
            </div>
          </div>

          <SaaSAttendanceCalendarSuite heatmapData={analyticsData?.heatmap} dynamicTierOptions={dynamicTierDropdownOptions} />
        </motion.div>

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 5: TOP PERFORMING CONTENT TABLE WITH PAGINATION & SEARCH  */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }} className="rounded-[28px] overflow-hidden mb-6" style={glass}>
          {/* Table Header Controls */}
          <div className="ad-pad flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 border-b border-zinc-200/70">
            <div>
              <p className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                Top Performing Content & Videos
              </p>
              <p className="text-[12px] font-semibold text-zinc-400 mt-0.5">
                Practice video library and access details ({allContentVideos.length} total videos in DB)
              </p>
            </div>

            <div className="ad-filters flex flex-wrap items-center gap-3">
              {/* Search Box */}
              <div className="ad-filters-full relative">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={13} />
                <input
                  type="text"
                  value={tableSearch}
                  onChange={(e) => {
                    setTableSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search videos..."
                  className="pl-9 pr-3.5 py-2 rounded-full text-[12px] font-semibold bg-zinc-50 border border-zinc-200 focus:outline-none focus:border-amber-500 w-40 sm:w-48 shadow-2xs"
                />
              </div>

              {/* Dynamic Luxury Custom Tier Filter Dropdown */}
              <CustomDropdown
                value={tierFilter}
                options={dynamicTierDropdownOptions}
                onChange={(val) => {
                  setTierFilter(val);
                  setCurrentPage(1);
                }}
                placeholder="Filter Tier"
                width="w-40"
              />

              {/* Custom Luxury Page Size Selector Dropdown */}
              <CustomDropdown
                value={pageSize}
                options={pageSizeOptions}
                onChange={(val) => {
                  setPageSize(val === "all" ? "all" : Number(val));
                  setCurrentPage(1);
                }}
                placeholder="Rows per page"
                width="w-36"
              />

              {/* Export Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleExportContentReport}
                className="h-9 px-4 rounded-full text-[11.5px] font-bold flex items-center gap-1.5 bg-white border border-amber-900/15 text-zinc-700 cursor-pointer shadow-2xs hover:bg-amber-50 transition-all"
              >
                <FiDownload size={13} className="text-amber-600" /> Export CSV
              </motion.button>
            </div>
          </div>

          {/* Table Body */}
          <div className="ad-table overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-amber-50/50 border-b border-amber-200/60">
                  {["#", "Video Title", "Category", "Access Tier", "Duration", "Status"].map((h) => (
                    <th
                      key={h}
                      className={`text-[11px] font-black uppercase tracking-[0.9px] text-zinc-400 py-3.5 px-5 ${
                        h === "#" || h === "Status" ? "text-center" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedContent.length > 0 ? (
                  paginatedContent.map((c, i) => {
                    const rowIdx = pageSize === "all" ? i : (currentPage - 1) * effectivePageSize + i;
                    const theme = TIER_THEMES[c.tier.toLowerCase()] || TIER_THEMES["standard"];

                    return (
                      <motion.tr
                        key={c.id || i}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.02 }}
                        className="border-b border-zinc-100 hover:bg-amber-50/30 transition-colors"
                      >
                        {/* Rank Badge */}
                        <td className="py-3.5 px-5 text-center">
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center mx-auto text-[12px] font-black"
                            style={{
                              background: rowIdx === 0 ? "rgba(242,154,12,0.18)" : rowIdx === 1 ? "rgba(107,138,90,0.18)" : "rgba(239,229,216,0.6)",
                              color: rowIdx === 0 ? T.orange : rowIdx === 1 ? T.olive : T.muted,
                              fontFamily: "'Poppins',sans-serif",
                            }}
                          >
                            {rowIdx + 1}
                          </div>
                        </td>

                        {/* Video Title */}
                        <td className="py-3.5 px-5">
                          <p className="text-[13.5px] font-bold text-[#1F1F1F]">{c.title}</p>
                        </td>

                        {/* Category */}
                        <td className="py-3.5 px-5">
                          <span className="text-[10.5px] font-extrabold px-2.5 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200">
                            {c.category}
                          </span>
                        </td>

                        {/* Access Tier */}
                        <td className="py-3.5 px-5">
                          <span
                            className="text-[10.5px] font-extrabold px-2.5 py-1 rounded-full"
                            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.color}30` }}
                          >
                            {c.tier}
                          </span>
                        </td>

                        {/* Duration */}
                        <td className="py-3.5 px-5 text-[12.5px] font-semibold text-zinc-600">
                          {c.duration || "30 mins"}
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-5 text-center">
                          <span className="text-[10.5px] font-extrabold px-3 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                            Published
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-[13px] font-semibold text-zinc-400">
                      No video performance records match your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Table Pagination Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-zinc-200/70 bg-zinc-50/50">
            <div className="text-[12px] font-bold text-zinc-500">
              Showing <span className="text-zinc-900">{filteredContent.length > 0 ? (currentPage - 1) * effectivePageSize + 1 : 0}</span> to{" "}
              <span className="text-zinc-900">{pageSize === "all" ? filteredContent.length : Math.min(currentPage * effectivePageSize, filteredContent.length)}</span> of{" "}
              <span className="text-zinc-900">{filteredContent.length}</span> videos
            </div>

            {pageSize !== "all" && totalPages > 1 && (
              <div className="flex flex-wrap items-center gap-2">
                {/* Active Page Counter Pill */}
                <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                  Page {currentPage} of {totalPages}
                </span>

                {/* Jump to First Page Button */}
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(1)}
                  title="First Page"
                  className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                >
                  <FiChevronsLeft size={14} />
                </button>

                {/* Previous Page Button */}
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                  className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                >
                  <FiChevronLeft size={14} /> Prev
                </button>

                {/* Smart Windowed Page Number Buttons */}
                <div className="flex items-center gap-1">
                  {(() => {
                    let pages = [];
                    if (totalPages <= 7) {
                      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                    } else if (currentPage <= 4) {
                      pages = [1, 2, 3, 4, 5, "...", totalPages];
                    } else if (currentPage >= totalPages - 3) {
                      pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                    } else {
                      pages = [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
                    }

                    return pages.map((item, idx) => {
                      if (item === "...") {
                        return (
                          <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                            •••
                          </span>
                        );
                      }

                      const isActive = currentPage === item;
                      return (
                        <button
                          key={item}
                          onClick={() => setCurrentPage(item)}
                          className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                            isActive
                              ? "bg-amber-500 text-white shadow-xs"
                              : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                          }`}
                        >
                          {item}
                        </button>
                      );
                    });
                  })()}
                </div>

                {/* Next Page Button */}
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                  className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                >
                  Next <FiChevronRight size={14} />
                </button>

                {/* Jump to Last Page Button */}
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  title="Last Page"
                  className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                >
                  <FiChevronsRight size={14} />
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* SECTION 6: EXECUTIVE INSIGHTS & GROWTH DIAGNOSTICS PODS            */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="flex items-center gap-2.5">
                <p className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
                  Executive Insights & Diagnostics
                </p>
                <span className="text-[10px] font-black text-amber-800 bg-amber-100 px-2.5 py-0.5 rounded-full">
                  AI RECOMMENDATIONS
                </span>
              </div>
              <p className="text-[12px] font-semibold text-zinc-400 mt-0.5">
                Automated takeaways from live member attendance and subscription trends
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Card 1: Revenue Conversion */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
              whileHover={{ y: -4, boxShadow: "0 24px 55px rgba(242,154,12,0.12)" }}
              className="rounded-[28px] p-7 border flex flex-col justify-between transition-all"
              style={{
                background: "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,247,237,0.85))",
                borderColor: "rgba(242,154,12,0.25)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
              }}
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-[18px] bg-amber-500/15 flex items-center justify-center text-amber-600 shadow-2xs">
                    <FiAward size={22} />
                  </div>
                  <span className="text-[10.5px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-amber-100/80 text-amber-900 border border-amber-300">
                    Revenue Leader
                  </span>
                </div>

                <h4 className="font-black text-[16px] text-zinc-900 font-poppins mb-2 leading-snug">
                  {insights.leader && insights.leader.n > 0
                    ? `${insights.leader.tier} Tier Leading Conversion`
                    : "No Paid Subscribers Yet"}
                </h4>
                <p className="text-[13px] font-medium text-zinc-600 leading-relaxed">
                  {insights.leader && insights.leader.n > 0 ? (
                    <>{insights.leader.tier} holds <strong className="text-zinc-900 font-bold">{insights.leaderPct}%</strong> of all active paying subscribers.</>
                  ) : (
                    <>Paid subscriber share will appear here once members subscribe to a paid plan.</>
                  )}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-amber-200/60">
                <div className="flex items-center justify-between text-[11.5px] font-bold text-zinc-600 mb-1.5">
                  <span>Paid Subscriber Share</span>
                  <span className="font-black text-amber-700 font-poppins">{insights.leaderPct}%</span>
                </div>
                <div className="h-[6px] rounded-full overflow-hidden bg-amber-200/50">
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${insights.leaderPct}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    style={{ background: `linear-gradient(90deg, ${T.orange}, ${T.orange2})` }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Card 2: Platform Retention */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              whileHover={{ y: -4, boxShadow: "0 24px 55px rgba(107,138,90,0.12)" }}
              className="rounded-[28px] p-7 border flex flex-col justify-between transition-all"
              style={{
                background: "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(244,248,241,0.85))",
                borderColor: "rgba(107,138,90,0.25)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
              }}
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-[18px] bg-emerald-500/15 flex items-center justify-center text-emerald-600 shadow-2xs">
                    <FiShield size={22} />
                  </div>
                  <span className="text-[10.5px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-100/80 text-emerald-900 border border-emerald-300">
                    Platform Health
                  </span>
                </div>

                <h4 className="font-black text-[16px] text-zinc-900 font-poppins mb-2 leading-snug">
                  Retention Ratio ({insights.activePct}%)
                </h4>
                <p className="text-[13px] font-medium text-zinc-600 leading-relaxed">
                  <strong className="text-zinc-900 font-bold">{activeAccounts} out of {totalAccounts}</strong> accounts are actively attending yoga sessions and practicing with unexpired active access.
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-emerald-200/60">
                <div className="flex items-center justify-between text-[11.5px] font-bold text-zinc-600 mb-1.5">
                  <span>Active Retention Rate</span>
                  <span className="font-black text-emerald-700 font-poppins">{activeAccounts} / {totalAccounts} Users</span>
                </div>
                <div className="h-[6px] rounded-full overflow-hidden bg-emerald-200/50">
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${insights.activePct}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    style={{ background: `linear-gradient(90deg, ${T.olive}, ${T.olive2})` }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Card 3: Optimal Slots */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.36 }}
              whileHover={{ y: -4, boxShadow: "0 24px 55px rgba(83,74,183,0.12)" }}
              className="rounded-[28px] p-7 border flex flex-col justify-between transition-all"
              style={{
                background: "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(248,247,255,0.85))",
                borderColor: "rgba(83,74,183,0.25)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
              }}
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-[18px] bg-purple-500/15 flex items-center justify-center text-purple-600 shadow-2xs">
                    <FiClock size={22} />
                  </div>
                  <span className="text-[10.5px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-purple-100/80 text-purple-900 border border-purple-300">
                    Attendance Peak
                  </span>
                </div>

                <h4 className="font-black text-[16px] text-zinc-900 font-poppins mb-2 leading-snug">
                  {peakSlotInfo ? "Optimal Attendance Slots" : "No Attendance Peak Yet"}
                </h4>
                <p className="text-[13px] font-medium text-zinc-600 leading-relaxed">
                  {peakSlotInfo ? (
                    <>Live attendance spikes at <strong className="text-zinc-900 font-bold">{peakSlotInfo}</strong>. Scheduling new sessions in these windows maximizes attendance.</>
                  ) : (
                    <>Peak attendance windows will appear here once members start checking in to live sessions.</>
                  )}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-purple-200/60">
                <div className="flex items-center justify-between text-[11.5px] font-bold text-zinc-600 mb-1.5">
                  <span>Peak Windows</span>
                  <span className="font-black text-purple-700 font-poppins">{peakSlotInfo || "0"}</span>
                </div>
                <div className="h-[6px] rounded-full overflow-hidden bg-purple-200/50">
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: peakSlotInfo ? "100%" : "0%" }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    style={{ background: "linear-gradient(90deg, #8B5CF6, #6D28D9)" }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Floating Notification Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div
              key={toast}
              initial={{ opacity: 0, y: 16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16 }}
              className="ad-toast fixed bottom-7 right-7 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-[18px] text-white text-[13px] font-bold shadow-2xl"
              style={{ background: "linear-gradient(135deg,#1F1F1F,#2D2D2D)", border: "1px solid rgba(255,255,255,0.15)" }}
            >
              <FiCheckCircle size={17} style={{ color: T.olive }} />
              {toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  </AdminDashboardLayout>
);
}
